/**
 * Turns a connected application's call log rows into something the
 * Connections page can label.
 *
 * Tool names mirror the backend's MCP registry (`internal/mcp/tools_*.go`).
 * Each has a label under `dashboard.account.connections.tools`; a tool this
 * build does not know is shown by its name rather than hidden.
 */
export const AGENT_TOOLS = [
  'account_get',
  'subscription_list',
  'domain_list',
  'domain_get',
  'domain_search',
  'domain_tld_catalogue',
  'domain_tld_price',
  'dns_record_list',
  'dns_record_create',
  'dns_record_update',
  'dns_record_delete',
  'mail_account_list',
  'mail_account_get',
  'mailbox_create',
  'mail_forward_create',
  'mail_account_update',
  'mail_account_delete',
  'mailbox_password_link',
  'mail_vacation_get',
  'mail_vacation_set',
  'mail_catchall_get',
  'mail_catchall_set',
  'mail_catchall_disable',
  'mail_dns_status',
  'mail_dns_apply',
  'mail_dns_replace',
  'handle_list',
  'handle_create',
  'handle_update',
  'handle_verify_request',
  'handle_verify_confirm',
  'handle_verify_status',
  'grant_get',
  'grant_revoke'
] as const

export type AgentTool = (typeof AGENT_TOOLS)[number]

export const isKnownAgentTool = (tool: string): tool is AgentTool =>
  (AGENT_TOOLS as readonly string[]).includes(tool)

/**
 * The names a row keeps, in the order a row shows them, each with a label
 * under `dashboard.account.connections.activity.args`: the arguments tools log
 * (`withLoggedArgs` in the backend registry) and the values they record beside
 * them (`withRecordable`), with any `previous_` prefix taken off. Others follow
 * by name, under their own name.
 */
export const AGENT_ARG_NAMES = [
  'domain',
  'address',
  'id',
  'mailbox_id',
  'channel',
  'type',
  'name',
  'content',
  'ttl',
  'priority',
  'country',
  'active',
  'targets',
  'enabled',
  'from_date',
  'to_date',
  'subject',
  'message',
  'deleted_address',
  'deleted_type'
] as const

/**
 * Values with a label of their own, under
 * `dashboard.account.connections.activity.values.<name>.<value>`.
 */
export const AGENT_ARG_VALUES: Readonly<Partial<Record<string, readonly string[]>>> = {
  channel: ['email', 'phone'],
  deleted_type: ['mailbox', 'alias']
}

/** The prefix of a value a tool recorded beside its arguments: what a change replaced. */
const PREVIOUS_PREFIX = 'previous_'

/**
 * The addresses outside the account's domains a call asked to forward mail
 * to, recorded before it ran whatever its outcome, which the row shows apart
 * from the other values.
 */
const EXTERNAL_TARGETS_KEY = 'external_targets'

/** Set when the backend cut or left out values to fit the row. */
const TRUNCATED_KEY = '_truncated'

/**
 * A recorded string longer than the backend keeps whole is kept as
 * `<name>_bytes` and `<name>_sha256` instead (calllog `record`).
 */
const BYTES_SUFFIX = '_bytes'
const SHA256_SUFFIX = '_sha256'
const SHA256_HEX = /^[0-9a-f]{64}$/

/** A recorded value too long to keep, as its length and digest. */
export interface AgentOversize {
  bytes: number
  sha256: string
}

export type AgentArgValue = string | number | boolean | string[] | AgentOversize

export const isOversize = (value: AgentArgValue): value is AgentOversize =>
  typeof value === 'object' && !Array.isArray(value)

export const hasAgentValueLabel = (name: string, value: AgentArgValue): boolean =>
  typeof value === 'string' && (AGENT_ARG_VALUES[name]?.includes(value) ?? false)

export interface AgentArg {
  /** The argument name, without the `previous_` prefix of a recorded value. */
  name: string
  /** Whether `name` has a label. */
  known: boolean
  value: AgentArgValue
}

export interface AgentArgs {
  given: AgentArg[]
  previous: AgentArg[]
  /** Addresses outside the account's domains the call asked to forward to. */
  external: string[]
  truncated: boolean
}

/** The value as the row keeps it, or null for a shape the backend never writes. */
const keptValue = (value: unknown): AgentArgValue | null => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return value
  return null
}

/**
 * The row's values by name, each `<name>_bytes` and `<name>_sha256` pair
 * folded back into `<name>`.
 */
const keptEntries = (args: Record<string, unknown>): Array<[string, AgentArgValue]> => {
  const entries: Array<[string, AgentArgValue]> = []
  const folded = new Set<string>()
  for (const [key, bytes] of Object.entries(args)) {
    if (!key.endsWith(BYTES_SUFFIX)) continue
    const name = key.slice(0, -BYTES_SUFFIX.length)
    const digestKey = name + SHA256_SUFFIX
    const sha256 = args[digestKey]
    if (Object.hasOwn(args, name)) continue
    if (typeof bytes !== 'number' || !Number.isInteger(bytes)) continue
    if (typeof sha256 !== 'string' || !SHA256_HEX.test(sha256)) continue
    entries.push([name, { bytes, sha256 }])
    folded.add(key).add(digestKey)
  }
  for (const [key, raw] of Object.entries(args)) {
    if (key === TRUNCATED_KEY || folded.has(key)) continue
    const value = keptValue(raw)
    if (value !== null) entries.push([key, value])
  }
  return entries
}

const argOrder = (name: string): number => {
  const index = (AGENT_ARG_NAMES as readonly string[]).indexOf(name)
  return index === -1 ? AGENT_ARG_NAMES.length : index
}

const byArgOrder = (a: AgentArg, b: AgentArg): number =>
  argOrder(a.name) - argOrder(b.name) || a.name.localeCompare(b.name)

/**
 * Splits a row's kept arguments into what the call was given, what it
 * replaced and where outside the account it asked to forward mail, and
 * reports whether anything was cut to fit.
 */
export const splitAgentArgs = (args: Record<string, unknown> | null | undefined): AgentArgs => {
  const given: AgentArg[] = []
  const previous: AgentArg[] = []
  let external: string[] = []
  for (const [key, value] of keptEntries(args ?? {})) {
    if (key === EXTERNAL_TARGETS_KEY && Array.isArray(value)) {
      external = value
      continue
    }
    const recorded = key.startsWith(PREVIOUS_PREFIX)
    const name = recorded ? key.slice(PREVIOUS_PREFIX.length) : key
    const arg = { name, known: (AGENT_ARG_NAMES as readonly string[]).includes(name), value }
    if (recorded) previous.push(arg)
    else given.push(arg)
  }
  return {
    given: [...given].sort(byArgOrder),
    previous: [...previous].sort(byArgOrder),
    external,
    truncated: args?.[TRUNCATED_KEY] === true
  }
}
